import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Paper,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Container,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  PersonOutline as PersonIcon,
  PendingActions as PendingIcon
} from '@mui/icons-material';
import * as homeContentApi from '../../services/homeContent';
import * as testimonialApi from '../../services/testimonial';
import * as adminApi from '../../services/admin';

const MotionCard = motion(Card);
const MotionPaper = motion(Paper);

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const AdminDashboard: React.FC = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  // Stats
  const [stats, setStats] = useState<any>(null);

  // Featured content state
  const [featuredExperts, setFeaturedExperts] = useState<any[]>([]);
  const [featuredIndustries, setFeaturedIndustries] = useState<any[]>([]);
  const [featuredTestimonials, setFeaturedTestimonials] = useState<any[]>([]);

  // Available resources state
  const [availableExperts, setAvailableExperts] = useState<any[]>([]);
  const [availableIndustries, setAvailableIndustries] = useState<any[]>([]);
  const [availableTestimonials, setAvailableTestimonials] = useState<any[]>([]);

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [homeContent, statsData] = await Promise.all([
        homeContentApi.getAllHomeContent(),
        adminApi.getStats()
      ]);

      setStats(statsData);

      // Process home content by type
      const experts = homeContent.filter(item => item.contentType === 'featured_experts');
      const industries = homeContent.filter(item => item.contentType === 'featured_industries');
      const testimonials = homeContent.filter(item => item.contentType === 'featured_testimonials');

      setFeaturedExperts(experts);
      setFeaturedIndustries(industries);
      setFeaturedTestimonials(testimonials);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      showSnackbar('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableResources = async (contentType: 'featured_experts' | 'featured_industries' | 'featured_testimonials') => {
    try {
      if (contentType === 'featured_testimonials') {
        // For testimonials, get all approved ones
        const result = await testimonialApi.getAllTestimonials({ status: 'approved' });
        // Filter out already featured ones
        const featuredIds = featuredTestimonials.map(t => t.resourceId);
        setAvailableTestimonials(result.testimonials.filter(t => !featuredIds.includes(t.id)));
      } else {
        const resources = await homeContentApi.getAvailableResources(contentType);
        if (contentType === 'featured_experts') {
          setAvailableExperts(resources);
        } else {
          setAvailableIndustries(resources);
        }
      }
    } catch (error) {
      console.error('Error loading available resources:', error);
      showSnackbar('Failed to load available resources', 'error');
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddContent = async (contentType: 'featured_experts' | 'featured_industries' | 'featured_testimonials', resourceId: string, displayOrder?: number) => {
    try {
      await homeContentApi.addHomeContent({
        contentType,
        resourceId,
        displayOrder: displayOrder || 0
      });
      showSnackbar('Content added successfully');
      loadData();
      setAddDialogOpen(false);
    } catch (error: any) {
      console.error('Error adding content:', error);
      showSnackbar(error.response?.data?.error || 'Failed to add content', 'error');
    }
  };

  const handleRemoveContent = async (id: string) => {
    if (!window.confirm('Are you sure you want to remove this from featured content?')) return;

    try {
      await homeContentApi.deleteHomeContent(id);
      showSnackbar('Content removed successfully');
      loadData();
    } catch (error) {
      console.error('Error removing content:', error);
      showSnackbar('Failed to remove content', 'error');
    }
  };

  const handleUpdateOrder = async (id: string, direction: 'up' | 'down', currentOrder: number, items: any[]) => {
    const newOrder = direction === 'up' ? currentOrder - 1 : currentOrder + 1;
    if (newOrder < 0 || newOrder >= items.length) return;

    try {
      // Find item to swap with
      const swapItem = items.find(item => item.displayOrder === newOrder);

      // Update both items
      const updates = [
        { id, displayOrder: newOrder },
        ...(swapItem ? [{ id: swapItem.id, displayOrder: currentOrder }] : [])
      ];

      await homeContentApi.reorderHomeContent(updates);
      showSnackbar('Order updated successfully');
      loadData();
    } catch (error) {
      console.error('Error updating order:', error);
      showSnackbar('Failed to update order', 'error');
    }
  };

  // Tab panels
  const ExpertsPanel = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Featured Experts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {featuredExperts.length} experts featured on homepage
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            loadAvailableResources('featured_experts');
            setAddDialogOpen(true);
          }}
          sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}
        >
          Add Expert
        </Button>
      </Box>

      <Grid container spacing={3}>
        {featuredExperts.map((item) => {
          const expert = item.resource || {};
          return (
            <Grid item xs={12} md={6} lg={4} key={item.id}>
              <MotionCard
                whileHover={{ y: -4 }}
                sx={{
                  borderRadius: 3,
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton size="small" onClick={() => handleUpdateOrder(item.id, 'up', item.displayOrder, featuredExperts)} disabled={item.displayOrder === 0}>
                        <ArrowUpIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleUpdateOrder(item.id, 'down', item.displayOrder, featuredExperts)} disabled={item.displayOrder === featuredExperts.length - 1}>
                        <ArrowDownIcon />
                      </IconButton>
                      <Chip label={`#${item.displayOrder + 1}`} size="small" color="primary" />
                    </Box>
                    <IconButton size="small" color="error" onClick={() => handleRemoveContent(item.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                    <Avatar
                      src={expert.photoUrl}
                      sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}
                    >
                      {expert.firstName?.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight={600}>
                        {expert.firstName} {expert.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {expert.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {expert.organizationName}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </MotionCard>
            </Grid>
          );
        })}
      </Grid>

      {/* Add Expert Dialog */}
      <Dialog
        open={addDialogOpen && tabValue === 0}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>Add Featured Expert</Typography>
            <IconButton onClick={() => setAddDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {availableExperts.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No available experts to feature
            </Typography>
          ) : (
            <List>
              {availableExperts.map((expert) => (
                <ListItem key={expert.id} disablePadding>
                  <ListItemButton onClick={() => handleAddContent('featured_experts', expert.id, featuredExperts.length)}>
                    <Avatar src={expert.photoUrl} sx={{ mr: 2 }}>
                      {expert.firstName?.charAt(0)}
                    </Avatar>
                    <ListItemText
                      primary={`${expert.firstName} ${expert.lastName}`}
                      secondary={`${expert.title} - ${expert.organizationName}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );

  const IndustriesPanel = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Featured Industries
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {featuredIndustries.length} industries featured on homepage
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            loadAvailableResources('featured_industries');
            setAddDialogOpen(true);
          }}
          sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}
        >
          Add Industry
        </Button>
      </Box>

      <Grid container spacing={3}>
        {featuredIndustries.map((item) => {
          const industry = item.resource || {};
          return (
            <Grid item xs={12} md={6} lg={4} key={item.id}>
              <MotionCard
                whileHover={{ y: -4 }}
                sx={{
                  borderRadius: 3,
                  border: `2px solid ${alpha(theme.palette.success.main, 0.2)}`
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton size="small" onClick={() => handleUpdateOrder(item.id, 'up', item.displayOrder, featuredIndustries)} disabled={item.displayOrder === 0}>
                        <ArrowUpIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleUpdateOrder(item.id, 'down', item.displayOrder, featuredIndustries)} disabled={item.displayOrder === featuredIndustries.length - 1}>
                        <ArrowDownIcon />
                      </IconButton>
                      <Chip label={`#${item.displayOrder + 1}`} size="small" color="primary" />
                    </Box>
                    <IconButton size="small" color="error" onClick={() => handleRemoveContent(item.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {industry.name}
                  </Typography>
                  <Chip label={industry.category} size="small" color="info" variant="outlined" sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    {industry.description?.substring(0, 100)}...
                  </Typography>
                </CardContent>
              </MotionCard>
            </Grid>
          );
        })}
      </Grid>

      {/* Add Industry Dialog */}
      <Dialog
        open={addDialogOpen && tabValue === 1}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>Add Featured Industry</Typography>
            <IconButton onClick={() => setAddDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {availableIndustries.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No available industries to feature
            </Typography>
          ) : (
            <List>
              {availableIndustries.map((industry) => (
                <ListItem key={industry.id} disablePadding>
                  <ListItemButton onClick={() => handleAddContent('featured_industries', industry.id, featuredIndustries.length)}>
                    <ListItemText
                      primary={industry.name}
                      secondary={`${industry.category} - ${industry.marketSize || 'N/A'}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );

  const TestimonialsPanel = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Featured Testimonials
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {featuredTestimonials.length} testimonials featured on homepage
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            loadAvailableResources('featured_testimonials');
            setAddDialogOpen(true);
          }}
          sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}
        >
          Add Testimonial
        </Button>
      </Box>

      <Grid container spacing={3}>
        {featuredTestimonials.map((item) => {
          const testimonial = item.resource || {};
          const user = testimonial.user || {};
          return (
            <Grid item xs={12} md={6} key={item.id}>
              <MotionCard
                whileHover={{ y: -4 }}
                sx={{
                  borderRadius: 3,
                  border: `2px solid ${alpha(theme.palette.warning.main, 0.2)}`
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton size="small" onClick={() => handleUpdateOrder(item.id, 'up', item.displayOrder, featuredTestimonials)} disabled={item.displayOrder === 0}>
                        <ArrowUpIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleUpdateOrder(item.id, 'down', item.displayOrder, featuredTestimonials)} disabled={item.displayOrder === featuredTestimonials.length - 1}>
                        <ArrowDownIcon />
                      </IconButton>
                      <Chip label={`#${item.displayOrder + 1}`} size="small" color="primary" />
                    </Box>
                    <IconButton size="small" color="error" onClick={() => handleRemoveContent(item.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'start', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {user.firstName?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {user.firstName} {user.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user.organizationName || user.title}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {testimonial.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {testimonial.content?.substring(0, 150)}...
                  </Typography>
                </CardContent>
              </MotionCard>
            </Grid>
          );
        })}
      </Grid>

      {/* Add Testimonial Dialog */}
      <Dialog
        open={addDialogOpen && tabValue === 2}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>Add Featured Testimonial</Typography>
            <IconButton onClick={() => setAddDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {availableTestimonials.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No approved testimonials available to feature
            </Typography>
          ) : (
            <List>
              {availableTestimonials.map((testimonial) => (
                <ListItem key={testimonial.id} disablePadding>
                  <ListItemButton onClick={() => handleAddContent('featured_testimonials', testimonial.id, featuredTestimonials.length)}>
                    <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                      {testimonial.user?.firstName?.charAt(0)}
                    </Avatar>
                    <ListItemText
                      primary={testimonial.title}
                      secondary={`by ${testimonial.user?.firstName} ${testimonial.user?.lastName}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Hero Section */}
      <MotionPaper
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        elevation={0}
        sx={{
          mb: 6,
          p: { xs: 4, md: 6 },
          borderRadius: 4,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.secondary.main, 0.8)})`,
          color: 'white'
        }}
      >
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <DashboardIcon sx={{ fontSize: 48 }} />
          Homepage Content Management
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9 }}>
          Control what users see on the homepage - Featured Experts, Industries, and Success Stories
        </Typography>
      </MotionPaper>

      {/* Statistics Overview */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MotionCard whileHover={{ y: -4 }} sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {stats.totalUsers}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">Total Users</Typography>
              </CardContent>
            </MotionCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MotionCard whileHover={{ y: -4 }} sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PersonIcon sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    {stats.totalExperts}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">Total Experts</Typography>
              </CardContent>
            </MotionCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MotionCard whileHover={{ y: -4 }} sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <BusinessIcon sx={{ mr: 1, color: 'info.main' }} />
                  <Typography variant="h4" fontWeight={700} color="info.main">
                    {stats.totalIndustries}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">Total Industries</Typography>
              </CardContent>
            </MotionCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MotionCard whileHover={{ y: -4 }} sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.error.main, 0.2)}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PendingIcon sx={{ mr: 1, color: 'error.main' }} />
                  <Typography variant="h4" fontWeight={700} color="error.main">
                    {stats.pendingTestimonials}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">Pending Testimonials</Typography>
              </CardContent>
            </MotionCard>
          </Grid>
        </Grid>
      )}

      {/* Content Management Tabs */}
      <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            '& .MuiTab-root': {
              minHeight: 72,
              px: 4,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem'
            }
          }}
        >
          <Tab label="Featured Experts" icon={<PeopleIcon />} iconPosition="start" />
          <Tab label="Featured Industries" icon={<BusinessIcon />} iconPosition="start" />
          <Tab label="Featured Testimonials" icon={<StarIcon />} iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 4 }}>
          {tabValue === 0 && <ExpertsPanel />}
          {tabValue === 1 && <IndustriesPanel />}
          {tabValue === 2 && <TestimonialsPanel />}
        </Box>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminDashboard;
